import { useSearchParams } from "react-router-dom";
// material
import { styled } from "@mui/material/styles";
import { Card, Container, Stack, Typography } from "@mui/material";

// components
import Page from "../../components/Page";
import MHidden from "../../components/MHidden";
import FindEmailForm from "./FindEmailForm";
import LoginForm from "./LoginForm";
import SignupRequestForm from "./SignupRequestForm";

// ----------------------------------------------------------------------

const RootStyle = styled(Page)(({ theme }) => ({
  [theme.breakpoints.up("md")]: {
    display: "flex",
  },
}));

const SectionStyle = styled(Card)(({ theme }) => ({
  width: "100%",
  maxWidth: 464,
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  margin: theme.spacing(2, 0, 2, 2),
}));

const ContentStyle = styled("div")(({ theme }) => ({
  maxWidth: 480,
  margin: "auto",
  display: "flex",
  minHeight: "100vh",
  flexDirection: "column",
  justifyContent: "center",
  padding: theme.spacing(12, 0),
}));

const EmText = styled("span")(({ theme }) => ({
  fontWeight: "bold",
  color: "#476CEC",
}));

// ----------------------------------------------------------------------

/**
 * 이 화면이 보여줄 수 있는 폼.
 *
 * 원래는 `toggleForgotPassword` 불리언 하나였다. 회원가입 신청까지 세 가지가 되면서
 * 불리언을 하나 더 두면 "둘 다 true" 같은 있을 수 없는 상태가 생긴다. 한 번에 하나만
 * 뜨는 게 이 화면의 성질이므로 값 하나로 표현한다.
 */
const AUTH_MODES = ["login", "findEmail", "signupRequest"] as const;
type AuthMode = (typeof AUTH_MODES)[number];

export default function Login() {
  /**
   * ★ 어느 폼인지를 **주소에 적는다**(`?form=signupRequest`).
   *
   * 예전에는 `useState` 였다. 그러면 회원가입 화면으로 넘어가도 **히스토리에 아무
   * 항목이 안 쌓인다** — 주소가 그대로이기 때문이다. 그 상태에서 브라우저 뒤로가기를
   * 누르면 «로그인 화면» 이 아니라 **앱 밖으로** 나간다. 주소를 직접 쳐서 들어온
   * 사람에게는 돌아갈 곳이 없어 흰 화면이 뜬다.
   *
   * 주소에 적으면 라우터가 뒤로가기를 알아서 처리한다 — 항목이 쌓이고, 뒤로 가면
   * 이전 폼으로 돌아온다. 덤으로 링크를 그대로 공유할 수 있다.
   *
   * 모르는 값이 오면 로그인으로 떨어뜨린다. 주소는 누구나 고칠 수 있으므로
   * `?form=아무거나` 로 빈 화면이 나오면 안 된다.
   */
  const [params, setParams] = useSearchParams();
  const asked = params.get("form");
  const mode: AuthMode = AUTH_MODES.includes(asked as AuthMode)
    ? (asked as AuthMode)
    : "login";
  const setMode = (next: AuthMode) =>
    setParams(next === "login" ? {} : { form: next });

  return (
    <RootStyle title="Login">
      <MHidden width="mdDown">
        <SectionStyle>
          <Stack
            direction="column"
            alignItems="center"
            textAlign="center"
            sx={{ mb: 10 }}
          >
            <Typography variant="h3" sx={{ px: 5, mt: 10, mb: 5 }}>
              프리즘 <EmText>B</EmText>ack <EmText>T</EmText>est
              <EmText> S</EmText>ystem에
              <br/>
              오신것을 환영합니다
            </Typography>
            <img
              src="/static/Frism_combination_default_vertical.png"
              alt="login"
              style={{ maxWidth: 250 }}
            />
          </Stack>
        </SectionStyle>
      </MHidden>

      <Container maxWidth="sm">
        <ContentStyle>
          <Stack direction="column" textAlign="center" spacing={4}>
            {mode === "login" && (
              <>
                <Typography variant="h4"></Typography>
                <LoginForm
                  onSignupRequest={() => setMode("signupRequest")}
                  onFindEmail={() => setMode("findEmail")}
                />
              </>
            )}
            {mode === "findEmail" && (
              <FindEmailForm onBack={() => setMode("login")}/>
            )}
            {mode === "signupRequest" && (
              <>
                <Typography variant="h4">회원가입 신청</Typography>
                <Typography variant="subtitle2">
                  이름과 이메일을 남겨주시면 <br/> 관리자 승인 후 메일로 알려드립니다.
                </Typography>
                <SignupRequestForm toggleForm={() => setMode("login")}/>
              </>
            )}
          </Stack>
        </ContentStyle>
      </Container>
    </RootStyle>
  );
}
